// widget/src/App.tsx
// Per spec §3.1 + §3.2 + §3.3: root component inside shadow root.
// Two-phase session flow per plan Task 27b note (probe on open + real on submit;
// probe session has lead_email=null so it never enrolls in followup — sits idle and TTLs cleanly).

import { useState, useRef, useEffect } from 'react'
import type { UIMessage } from 'ai'
import { ChatLauncher } from './components/ChatLauncher'
import { ChatPanel } from './components/ChatPanel'
import { PreChatForm } from './components/PreChatForm'
import { ReturningVisitorGreeting } from './components/ReturningVisitorGreeting'
import { MessageList } from './components/MessageList'
import { InputBar } from './components/InputBar'
import { RetryBubble } from './components/RetryBubble'
import { PoweredByMikla } from './components/PoweredByMikla'
import { VendorInfoProvider } from './context/VendorInfoContext'
import { useLeadCookie, type LeadCookieAPI } from './hooks/useLeadCookie'
import { useMiklaChat } from './hooks/useMiklaChat'
import { useCoalesce } from './hooks/useCoalesce'
import { postChatSession, type ChatSessionResponse } from './lib/api'

export interface AppProps {
  vendorId: string
  apiBase: string
  fetchImpl?: typeof fetch
}

type Phase = 'launcher' | 'loading' | 'form' | 'chat' | 'error'

type InputStatus = 'ready' | 'submitted' | 'streaming' | 'error'

export function App({ vendorId, apiBase, fetchImpl }: AppProps) {
  const [phase, setPhase] = useState<Phase>('launcher')
  const [probe, setProbe] = useState<ChatSessionResponse | null>(null)
  const [session, setSession] = useState<ChatSessionResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  // True ONLY when the lead cookie was present BEFORE handleOpen ran. The
  // PreChatForm writes a cookie on submit, so reading cookie state at chat-mount
  // time would mis-flag first-time visitors as returning. We must capture the
  // signal before the form has had a chance to mutate localStorage.
  const [wasReturningVisitor, setWasReturningVisitor] = useState(false)
  const cookie = useLeadCookie()
  const startedRef = useRef(false)

  const callSession = async (
    lead: { name?: string; email?: string; phone?: string; event_type?: string | null },
    extras: { is_returning_visitor?: boolean } = {},
  ) => {
    return postChatSession(
      apiBase,
      {
        vendor_id: vendorId,
        lead_email: lead.email,
        lead_name: lead.name,
        // Direction B 4-field welcome — see api.ts and useLeadCookie.ts comments
        // for why this round-trips through the backend even before persistence
        // is wired (extras='ignore' on the Pydantic model is the safety net).
        lead_phone: lead.phone,
        event_type: lead.event_type ?? undefined,
        // A7: pass through the device-returner signal only when the caller
        // sets it (currently the cookie-autosubmit path). Omitted otherwise
        // so first-time visitors and form-submit users don't tag every row.
        is_returning_visitor: extras.is_returning_visitor,
      },
      fetchImpl,
    )
  }

  const handleOpen = async () => {
    if (startedRef.current) return
    startedRef.current = true
    setPhase('loading')
    try {
      const c = cookie.read()
      if (c) {
        // Returning visitor: single call with full data, skip form.
        setWasReturningVisitor(true)
        const resp = await callSession(
          {
            name: c.name,
            email: c.email,
            phone: c.phone,
            event_type: c.event_type ?? null,
          },
          { is_returning_visitor: true },
        )
        setSession(resp)
        setPhase('chat')
      } else {
        // First-time visitor: probe to get domain_options + vendor_info, then show form.
        // Probe carries lead_email=null so backend never enrolls it in followup —
        // the probe row sits idle and TTLs cleanly per plan Task 27b note.
        setWasReturningVisitor(false)
        const resp = await callSession({})
        setProbe(resp)
        setPhase('form')
      }
    } catch (e) {
      setError((e as Error).message)
      setPhase('error')
      // Reset so a subsequent launcher click can retry from scratch.
      startedRef.current = false
    }
  }

  const handleFormSubmit = async (lead: {
    name: string
    email: string
    phone: string
    event_type: string | null
  }) => {
    setFormError(null) // clear any prior form error on retry
    setPhase('loading')
    try {
      const resp = await callSession(lead)
      setSession(resp)
      setPhase('chat')
    } catch (e) {
      // Stay on the form so the user can retry without the close-reopen-probe-form
      // 5-click recovery cycle. Surface the error inline above the form via formError.
      // The probe-fails-on-open path still routes to phase='error' (see handleOpen),
      // because in that case there's no form mounted yet and recovery requires reopen.
      setFormError((e as Error).message)
      setPhase('form')
    }
  }

  const handleClose = () => {
    setPhase('launcher')
    startedRef.current = false
  }

  if (phase === 'launcher') return <ChatLauncher onClick={handleOpen} />

  // Direction B "Ink Header" handoff: header carries vendor identity directly.
  // The title is the venue name; the status row beneath ("● HOW CAN I HELP?")
  // is a generic invitation. The legacy "Welcome to / Chatting with" prefixes
  // are removed — they double up with the venue title.
  const venueName =
    (phase === 'chat' && session?.vendor_info.business_name) ||
    (phase === 'form' && probe?.vendor_info.business_name) ||
    'Mikla Estate'
  // Keep the status row off the bare-error fallback since the surface isn't
  // a normal conversation when the session failed to load.
  const showStatus = phase !== 'error'

  // Vendor branding (theme_color/logo_url) is present on both the probe and
  // the real session response; prefer the real session once it lands. The
  // provider must wrap ChatPanel (not just the chat-phase subtree) because
  // the warm-ink header renders from the probe during the 'form' phase too.
  const vendorInfo = session?.vendor_info ?? probe?.vendor_info ?? null

  return (
    <VendorInfoProvider value={vendorInfo}>
      <ChatPanel
        onClose={handleClose}
        title={venueName}
        showStatus={showStatus}
      >
        {phase === 'loading' && (
          <div data-testid="loading" className="flex-1 p-6 text-sm text-muted-foreground bg-paper">
            Loading...
          </div>
        )}
        {phase === 'error' && error && (
          <div data-testid="app-error" className="flex-1 p-4 text-sm text-red-700 bg-red-50">
            {error}
          </div>
        )}
        {phase === 'form' && probe && (
          <>
            {formError && (
              <div data-testid="form-error" className="px-4 py-2 text-sm text-red-700 bg-red-50">
                {formError}
              </div>
            )}
            <PreChatForm
              domainOptions={probe.domain_options}
              venueName={venueName}
              fieldLabel={probe.domain_field_label}
              heading={probe.form_heading}
              onSubmit={handleFormSubmit}
            />
          </>
        )}
        {phase === 'chat' && session && (
          <SessionPanel
            session={session}
            apiBase={apiBase}
            fetchImpl={fetchImpl}
            cookie={cookie}
            wasReturningVisitor={wasReturningVisitor}
          />
        )}
        <PoweredByMikla />
      </ChatPanel>
    </VendorInfoProvider>
  )
}

interface SessionPanelProps {
  session: ChatSessionResponse
  apiBase: string
  fetchImpl?: typeof fetch
  cookie: LeadCookieAPI
  wasReturningVisitor: boolean
}

function SessionPanel({
  session,
  apiBase,
  fetchImpl,
  cookie,
  wasReturningVisitor,
}: SessionPanelProps) {
  const chat = useMiklaChat({
    apiBase,
    sessionId: session.session_id,
    // vendorId is not currently consumed downstream of useMiklaChat (transport keys off
    // session_id + apiBase); pass empty string to satisfy the option shape.
    vendorId: '',
    coalesceWindowMs: session.coalesce_window_ms,
    fetchImpl,
  })
  // V1 opening message: render the server-supplied greeting as the first
  // assistant bubble before the user types. The system prompt's OPENING
  // GREETING block tells Sonnet not to greet again on its first response,
  // so this bubble + Sonnet's reply don't double-greet the customer.
  // Synthetic message id (`opening-<session_id>`) so the bubble is stable
  // across renders and never collides with backend-generated assistant ids.
  useEffect(() => {
    if (!session.opening_message) return
    if (chat.messages.length > 0) return
    chat.setMessages([
      {
        id: 'opening-' + session.session_id,
        role: 'assistant',
        parts: [{ type: 'text', text: session.opening_message }],
      } as unknown as UIMessage,
    ])
    // Run only once per session; subsequent message activity is real chat state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.session_id])
  // Optimistic user message rendered while the coalesce window is buffering
  // (default 2500ms wait). Without this, single-message users hit Enter and
  // see an empty chat for 2.5s before their message + the dots appear. We clear
  // it the moment chat.sendMessage fires (which adds the user message to
  // chat.messages for real). Rapid-fire messages within the window concatenate
  // with `\n` to mirror the eventual coalesced POST shape.
  const [pendingUserText, setPendingUserText] = useState<string | null>(null)
  const coalesce = useCoalesce(chat.coalesceWindow, (text) => {
    setPendingUserText(null)
    chat.sendCoalesced(text)
  })
  // Only read the cookie if we knew about it at handleOpen time. PreChatForm
  // writes a cookie on submit, so a fresh-visit path would otherwise read
  // back the cookie it just wrote and falsely render the greeting.
  const cached = wasReturningVisitor ? cookie.read() : null

  return (
    <>
      {cached && <ReturningVisitorGreeting name={cached.name} />}
      <div className="flex-1 overflow-y-auto">
        <MessageList
          messages={chat.messages as UIMessage[]}
          status={chat.status as InputStatus}
          pendingUserText={pendingUserText}
        />
        {chat.showRetryBubble && (
          <div className="px-4">
            <RetryBubble onRetry={chat.manualRetry} />
          </div>
        )}
      </div>
      <InputBar
        status={chat.status as InputStatus}
        onSend={(t) => {
          setPendingUserText((prev) => (prev ? prev + '\n' + t : t))
          coalesce.queue(t)
        }}
        onStop={chat.stop}
      />
    </>
  )
}
